import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatter } from '@/components/AnalyticsDashboard/formatter'
import { formatNumber } from '@/utils/math'
import html2canvas from 'html2canvas'
import i18n from '@/i18n'
import { CoreEntityDto } from '@/interfaces/core.interface'
import Logo from '@/design-system/assets/images/Logo-Digital-Buildings-Black.png'

const margin = 20

export default function usePdfReport() {
  async function generatePdfReport(
    title: string,
    currentSectionName: string | undefined,
    entityPath: CoreEntityDto[][],
    dateInLocaleString: string,
    kpis,
    selectedKPI,
    coloredEntities,
    entitySource,
    clickedItemType: string,
    realmLogo: string
  ) {
    const { t } = i18n.global
    const { image, scaledHeight, scaledWidth } = await getMapAsImage()
    const doc = new jsPDF({
      format: 'a4',
      orientation: 'portrait',
      putOnlyUsedFonts: true,
      unit: 'mm'
    })
    let currentY = margin

    // Page 1 header
    setFont(doc, 'bold')
    currentY = addHeader(doc, currentY, title)
    setFont(doc, 'normal')

    // Page 1 logo
    if (realmLogo.length) await addRealmLogo(doc, realmLogo)

    // Page 1 content
    currentY = await addSelection(doc, currentY, entityPath, dateInLocaleString)
    if (kpis?.length) currentY = await addKpis(doc, currentY, kpis, selectedKPI)

    // If the map image will push the content to overlap the footer, add a new page for the image and legend
    if (currentY + scaledHeight > 230) {
      doc.addPage('a4', 'portrait')
      currentY = margin
      setFont(doc, 'bold')
      currentY = addHeader(doc, currentY, title)
      setFont(doc, 'normal')
    }
    // Add map, legend and disclaimer
    doc.addImage(image, 'PNG', margin, currentY, scaledWidth, scaledHeight)
    currentY = await addLegend(doc, currentY + scaledHeight)
    doc.setFontSize(9)
    doc.text('* ' + t('info__data-displayed'), margin, currentY + 8)

    // Add page(s) with table, if there is data
    if (coloredEntities !== undefined && coloredEntities[0]?.dataSource?.data !== undefined) {
      const body = getColoredEntityData(
        coloredEntities,
        entitySource,
        clickedItemType,
        currentSectionName
      )
      if (body.length) {
        doc.addPage('a4', 'portrait')
        addDataTables(doc, margin, body, title, clickedItemType, currentSectionName) // also handles headers for new pages
      }
    }

    // Add footers to all pages
    addFooters(doc)

    // Save the PDF
    doc.save(
      `${title.replaceAll(' - ', '-').replaceAll(' ', '-')}_${new Date()
        .toLocaleTimeString([], {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
        .replaceAll(' ', '_')
        .replaceAll(':', '-')}.pdf`
    )
  }
  return {
    generatePdfReport: generatePdfReport
  }
}

function addDataTables(doc: jsPDF, margin, body, title, clickedItemType, currentSectionName) {
  const { t } = i18n.global
  doc.setFontSize(11)
  function getColumnHeader() {
    if (currentSectionName === 'energy' || currentSectionName === 'portfolio')
      return t('general__text__building')
    switch (clickedItemType) {
      case 'building':
        return t('general__text__floor')
      case 'level':
        return t('general__text__room')
      case 'room':
        return t('general__text__room')
      default:
        return t('general__text__building') // Is this switch enough?
    }
  }
  const columnLabel =
    (clickedItemType === 'level' || clickedItemType === 'room') &&
    currentSectionName !== 'energy' &&
    currentSectionName !== 'portfolio' // Is this check enough foor rooms?
      ? t('general__text__room-number')
      : t('general__data__marking-label')
  const columns = [getColumnHeader(), columnLabel, 'KPI', t('general__text__value')]
  autoTable(doc, {
    head: [columns],
    body: body,
    margin: { top: margin + 8, right: margin, bottom: margin + 8, left: margin },
    startY: margin + 8,
    theme: 'plain',
    didDrawPage: () => {
      setFont(doc, 'bold')
      addHeader(doc, margin, title)
      setFont(doc, 'normal')
      doc.setFontSize(11)
    }
  })
}

function addFooters(doc: jsPDF) {
  const { t } = i18n.global
  const pageCount = doc.getNumberOfPages()
  const width = 585 // 585 is the width of the image in pixels
  const height = 261 // 261 is the height of the image in pixels
  const factor = 20 / width // 20 is the wanted width in mm
  doc.setFontSize(9)
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.addImage(
      Logo,
      'PNG',
      margin,
      297 - margin - height * factor + 5,
      width * factor,
      height * factor
    )
    const footerText = `${t('general__text__report-printed')} ${new Date().toLocaleTimeString([], {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })} ${t('general__text__from').toLowerCase()} Digital Buildings (${i}/${pageCount})`
    doc.text(footerText, 210 - margin, 297 - margin - 1, { align: 'right' })
  }
}

function addHeader(doc: jsPDF, currentY: number, title: string): number {
  doc.setFontSize(20)
  doc.text(title, margin, currentY)
  return currentY + 16
}

async function addKpis(
  doc: jsPDF,
  currentY: number,
  kpis,
  selectedKPI: string | undefined
): Promise<number> {
  const { locale } = i18n.global
  doc.setFontSize(11)
  let kpiY = 0
  kpis.forEach((kpi) => {
    if (kpi.config.name === selectedKPI) setFont(doc, 'bold')
    doc.text(
      kpi.config.title[locale.value] +
        ': ' +
        formatter(kpi.dataSource.data, locale.value, kpi.config.props.value.format),
      margin,
      currentY + kpiY
    )
    setFont(doc, 'normal')
    kpiY += 8
  })
  return currentY + kpiY
}

async function addLegend(doc: jsPDF, currentY: number): Promise<number> {
  const { t } = i18n.global
  let manualValues = false
  let element = document.getElementById('color-legend-continuous')
  // if (!element) element = document.getElementById('color-legend-categorical') // Currently this fails since the element is an svg
  if (!element) {
    element = document.getElementById('min-max-range-slider')
    if (element) manualValues = true // data-html2canvas-ignore used in min-max-range-slider to exclude the slider inputs (due to html2canvas styling issues)
  }
  if (!element) return currentY
  const canvas = await html2canvas(element, { scale: 2 })
  const image = canvas.toDataURL('image/webp')
  const factor = 90 / canvas.width // the wanted image width in mm
  doc.addImage(image, 'PNG', margin, currentY + 4, canvas.width * factor, canvas.height * factor)
  if (!manualValues) return canvas.height * factor + currentY + 4
  let textY = 0
  const sliderValues = element.getElementsByTagName('input')
  if (sliderValues) {
    doc.setFontSize(11)
    doc.text(
      `Min: ${formatNumber(
        Number(element.getElementsByTagName('input')[0].min),
        0
      )}. Max: ${formatNumber(Number(element.getElementsByTagName('input')[0].max), 0)}. ${t(
        'dashboard__text__limits-set'
      )}: ${formatNumber(
        Number(element.getElementsByTagName('input')[0].value),
        0
      )} - ${formatNumber(Number(element.getElementsByTagName('input')[1].value), 0)}.`,
      margin,
      canvas.height * factor + currentY + 8
    )
    textY += 8
  }
  return canvas.height * factor + currentY + textY
}
function getImageUrl(name, ext) {
  return `/src/assets/logo/${name}.${ext}`
}

async function addRealmLogo(doc: jsPDF, realmLogo: string) {
  let logo = realmLogo
  let logoWidth = 0
  let logoHeight = 0
  if (logo.toLowerCase().endsWith('svg')) {
    const realmId = localStorage.getItem('realmId')
    logo = realmId ? getImageUrl(realmId, 'png') : '' // Logo has to exist in png format
  }
  if (logo.length) {
    const img = new Image()
    img.onload = function () {
      logoWidth = img.width
      logoHeight = img.height
    }
    img.src = logo
    await new Promise((resolve) => setTimeout(resolve, 10)) // give the image time to load and set dimensions
    const factor = 25 / logoWidth // 25 is the wanted width in mm
    const positionX = 210 - margin - logoWidth * factor
    const positionY = margin - (logoHeight * factor) / 2
    doc.addImage(logo, 'PNG', positionX, positionY, logoWidth * factor, logoHeight * factor)
  }
}
async function addSelection(doc: jsPDF, currentY: number, entityPath, dateInLocaleString: string) {
  const { t } = i18n.global
  const entities: string[] = []
  const realmId = localStorage.getItem('realmId')
  if (entityPath.length)
    entities.push(entityPath.map((entities) => entities.map((e) => e.name).join(', ')).join(' - '))
  if (realmId && !entityPath.length) entities.push(realmId)
  doc.setFontSize(11)
  let addY = 0
  if (entities.length) {
    const placeText = entities.join(', ')
    doc.text(t('general__text__location') + ': ' + placeText, margin, currentY)
    addY += 8
  }

  if (dateInLocaleString?.length) {
    const dateText = dateInLocaleString ?? '-'
    doc.text(t('dashboard__text__time-interval') + ': ' + dateText, margin, currentY + addY)
    addY += 8
  }
  return currentY + addY + 8
}

function generateRoomNumber(roomEntity) {
  return roomEntity.nameTenant && roomEntity.labelTenant
    ? `${roomEntity.labelTenant} ${roomEntity.nameTenant}`
    : roomEntity.nameTenant && !roomEntity.labelTenant
    ? `${roomEntity.nameTenant}`
    : !roomEntity.nameTenant && roomEntity.labelTenant
    ? `${roomEntity.labelTenant}`
    : ''
}
function getColoredEntityData(coloredEntities, entitySource, clickedItemType, currentSectionName) {
  const rows = coloredEntities.flatMap((coloredEntity) => {
    return coloredEntity.dataSource.data.map((data) => {
      const entity = entitySource.find((entity) => entity.id === data.id)
      if (!entity) return undefined

      const entityName =
        (clickedItemType === 'level' || clickedItemType === 'room') &&
        currentSectionName !== 'energy' &&
        currentSectionName !== 'portfolio'
          ? entity.properties.displayName
          : entity.properties.name

      const entityLabel =
        (clickedItemType === 'level' || clickedItemType === 'room') &&
        currentSectionName !== 'energy' &&
        currentSectionName !== 'portfolio'
          ? generateRoomNumber(entity.properties)
          : entity.properties.marking

      const value = formatter(
        data.value,
        i18n.global.locale.value,
        coloredEntity.config.props.value.format
      )

      return [entityName, entityLabel, coloredEntity.config.title[i18n.global.locale.value], value]
    })
  })

  const validRows = rows.filter((row) => Array.isArray(row))
  return validRows.toSorted((a, b) => a[0].localeCompare(b[0]))
}

async function getMapAsImage() {
  const element = document.getElementsByClassName('mapboxgl-canvas')[0] as HTMLElement
  const canvas = await html2canvas(element)
  const image = canvas.toDataURL('image/webp')
  const finalWidth = 210 - margin * 2 // fill the width of the page in mm, minus margins
  const factor = finalWidth / canvas.width
  return {
    scaledWidth: canvas.width * factor,
    scaledHeight: canvas.height * factor,
    image: image
  }
}

function setFont(doc: jsPDF, style: string) {
  // doc.addFileToVFS('Inter-Medium.ttf', font)
  // doc.addFont('Inter-Medium.ttf', 'Inter', 'normal')
  doc.setFont('helvetica', style)
}
